import React, { useState, useRef, useEffect } from "react";
import {
  Download,
  Printer,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button, ButtonProps } from "./button";
import { cn } from "../../lib/utils";

export type ButtonVariant =
  | "default"
  | "destructive"
  | "outline"
  | "secondary"
  | "ghost"
  | "link"
  | "success"
  | "warning";

export type ButtonSize = "default" | "sm" | "lg" | "xl" | "icon";

export type ButtonIcon =
  | "download"
  | "print"
  | "plus"
  | "eye"
  | "edit"
  | "delete"
  | "search"
  | "filter"
  | "chevron-down"
  | "chevron-up"
  | "none";

interface AppButtonProps extends Omit<ButtonProps, "variant" | "size"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ButtonIcon;
  customIcon?: string;
  iconAlt?: string;
  iconPosition?: "left" | "right";
  isLoading?: boolean;
  fullWidth?: boolean;
  rounded?: "default" | "full" | "lg";
  style?: React.CSSProperties;
  className?: string;

  //* NEW: Dropdown support */
  dropdownItems?: { label: string; value: string }[];
  onDropdownSelect?: (value: string) => void;
}

const iconMap = {
  download: Download,
  print: Printer,
  plus: Plus,
  eye: Eye,
  edit: Edit,
  delete: Trash2,
  search: Search,
  filter: Filter,
  "chevron-down": ChevronDown,
  "chevron-up": ChevronUp,
  none: null,
};

const sizeMap = {
  sm: "h-8 px-3 text-xs",
  default: "h-10 py-2 px-4",
  lg: "h-12 px-6 text-lg",
  xl: "h-14 px-8 text-xl",
  icon: "h-10 w-10",
};

const variantMap: Record<ButtonVariant, string> = {
  default: "bg-primary text-primary-foreground hover:bg-primary/90",
  destructive:
    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  outline:
    "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  ghost: "hover:bg-accent hover:text-accent-foreground",
  link: "text-primary underline-offset-4 hover:underline",
  success: "bg-[#08453E] text-white hover:bg-green-700",
  warning: "bg-yellow-500 text-white hover:bg-yellow-600",
};

export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      children,
      variant = "default",
      size = "default",
      icon = "none",
      customIcon,
      iconAlt,
      iconPosition = "left",
      isLoading = false,
      fullWidth = false,
      rounded = "default",
      className,
      style,
      disabled,
      dropdownItems,
      onDropdownSelect,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const IconComponent = iconMap[icon];

    // Close dropdown when clicked outside
    useEffect(() => {
      const listener = (e: MouseEvent) => {
        if (!dropdownRef.current?.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", listener);
      return () => document.removeEventListener("mousedown", listener);
    }, []);

    const renderIcon = () => {
      if (isLoading)
        return (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        );

      if (customIcon) {
        return (
          <img
            src={customIcon}
            alt={iconAlt || "icon"}
            className="h-5 w-5 object-contain"
          />
        );
      }

      if (IconComponent && icon !== "none") {
        return <IconComponent className="h-4 w-4" />;
      }

      return null;
    };

    const buttonClass = cn(
      "inline-flex items-center justify-center gap-2 whitespace-nowrap min-w-[137px] min-h-11 rounded-2xl text-sm font-medium",
      variantMap[variant],
      sizeMap[size],
      fullWidth && "w-full",
      rounded === "full" && "rounded-full",
      rounded === "lg" && "rounded-lg",
      className,
    );

    return (
      <div className="relative inline-block self-end" ref={dropdownRef}>
        <Button
          ref={ref}
          className={buttonClass}
          style={style}
          disabled={disabled || isLoading}
          onClick={() => {
            if (dropdownItems) {
              setOpen(!open);
            } else {
              props.onClick?.(undefined as any);
            }
          }}
          {...props}
        >
          {iconPosition === "left" && renderIcon()}
          {children}
          {dropdownItems && <ChevronDown className="h-4 w-4 ml-1" />}
          {iconPosition === "right" && renderIcon()}
        </Button>

        {dropdownItems && open && (
          <div className="absolute right-0 mt-2 w-40 rounded-lg border bg-white shadow-lg z-50">
            {dropdownItems.map((item) => (
              <button
                key={item.value}
                onClick={() => {
                  setOpen(false);
                  onDropdownSelect?.(item.value);
                }}
                className="block w-full px-4 py-2 text-left hover:bg-gray-100"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

AppButton.displayName = "AppButton";
